import React, { useState, useEffect, useMemo } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Menu } from 'lucide-react';
import UnifiedSidebar from './UnifiedSidebar';
import { getUser } from '../utils/helpers';
import { useTeam } from '../lib/teamConfig';
import { TEAM_CONFIGS } from '../config/sportRoutes';
import { C } from './design-system/primitives/constants';

/**
 * SportLayout — Unified layout for all sport sections.
 *
 * Replaces BaseballLayout, BasketballLayout, FootballLayout,
 * HockeyLayout, GolfLayout, and SoccerLayout with a single
 * parameterized component.
 *
 * Receives sport as a prop from the route definition and configures:
 * - Team context switching
 * - Sport-specific sidebar
 * - Outlet context for child components
 */
export default function SportLayout({ sport }) {
  const location = useLocation();
  const navigate = useNavigate();
  const isEmulatorFrame = new URLSearchParams(location.search).get('mobileEmulator') === '1';
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [isMobile, setIsMobile] = useState(false);

  const { switchTeam, teamId } = useTeam();
  const teamConfig = useMemo(
    () => TEAM_CONFIGS[sport] || TEAM_CONFIGS.baseball,
    [sport]
  );

  // Redirect to login if not authenticated
  useEffect(() => {
    const user = getUser();
    if (!user || Object.keys(user).length === 0) {
      navigate('/login', { replace: true });
    }
  }, [navigate]);

  // Force correct team config for this sport
  useEffect(() => {
    if (teamConfig.validTeamIds) {
      if (!teamConfig.validTeamIds.includes(teamId)) {
        switchTeam(teamConfig.defaultTeamId);
      }
    } else if (teamId !== teamConfig.defaultTeamId) {
      switchTeam(teamConfig.defaultTeamId);
    }
  }, [switchTeam, teamId, teamConfig]);

  // Handle responsive sidebar — on mobile, hide completely
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      setIsSidebarOpen(!mobile);
    };
    window.addEventListener('resize', handleResize);
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Close mobile drawer on navigation
  useEffect(() => {
    if (isMobile) setIsSidebarOpen(false);
  }, [location.pathname, isMobile]);

  // Build outlet context — includes sport flag and sport name
  const outletContext = {
    sport,
    [teamConfig.sportFlag]: true,
    ...(isEmulatorFrame && { isEmbeddedMobile: true }),
  };

  if (isEmulatorFrame) {
    return (
      <div className="h-full max-h-full overflow-y-auto overflow-x-hidden bg-black text-white">
        <Outlet context={outletContext} />
      </div>
    );
  }

  return (
    <div className="flex h-full max-h-full overflow-hidden bg-black">
      {/* Mobile: overlay drawer */}
      {isMobile ? (
        <>
          {/* Backdrop */}
          {isSidebarOpen && (
            <div
              className="fixed inset-0 z-40 bg-black/60"
              onClick={() => setIsSidebarOpen(false)}
            />
          )}
          {/* Slide-in drawer */}
          <div
            className="fixed top-0 left-0 z-50 h-full transition-transform duration-300 ease-in-out"
            style={{ transform: isSidebarOpen ? 'translateX(0)' : 'translateX(-100%)' }}
          >
            <UnifiedSidebar
              sport={sport}
              isSidebarOpen={true}
              setIsSidebarOpen={setIsSidebarOpen}
            />
          </div>
        </>
      ) : (
        /* Desktop: inline sidebar */
        <UnifiedSidebar
          sport={sport}
          isSidebarOpen={isSidebarOpen}
          setIsSidebarOpen={setIsSidebarOpen}
        />
      )}
      <div className="flex-1 flex flex-col min-h-0 overflow-y-auto overflow-x-hidden bg-black text-white">
        {/* Mobile hamburger button */}
        {isMobile && !isSidebarOpen && (
          <button
            onClick={() => setIsSidebarOpen(true)}
            className="fixed top-3 left-3 z-30 p-2 rounded-lg"
            style={{ background: `${C.s2}CC`, border: `1px solid ${C.border}` }}
            aria-label="Open menu"
          >
            <Menu size={20} style={{ color: C.gold }} />
          </button>
        )}
        <Outlet context={outletContext} />
      </div>
    </div>
  );
}
